/**
 * The certification fields, in one component used by three callers.
 *
 * ── Why it is shared ──────────────────────────────────────────────────────────────────────
 *
 * The same facts are captured in three places: adding a completed certification on All
 * Certifications, applying for reimbursement, and recording a retake. If each built its own
 * fields they would drift on the rule that matters most — that a document is mandatory for a
 * FAILED result as well as a passed one — and the one that drifted would be the one somebody
 * used to file an unevidenced claim.
 *
 * Validation is not duplicated here either. `validateCertificationDraft` in types/certification
 * is the single statement of what a valid draft is, and it is the same function the service
 * calls before writing. This component only renders the fields and reports what was entered.
 */

import React, { useMemo, useState } from 'react';
import {
  CertificationDraft,
  CertificationResult,
  calculateRetakeDeadline,
} from '../../types/certification';
import {
  ALLOWED_CERTIFICATE_TYPES,
  MAX_CERTIFICATE_SIZE_MB,
  uploadCertificate,
  validateCertificateFile,
} from '../../services/certificationDocuments';
import './Certifications.css';

interface Props {
  value: Partial<CertificationDraft>;
  onChange: (next: Partial<CertificationDraft>) => void;
  /** Whose folder the document is uploaded into. The record's owner, not the actor. */
  ownerEmail: string;
  /** Errors from the shared validator, rendered above the actions by the parent. */
  errors?: string[];
  disabled?: boolean;
  /** Hides the name/provider fields when a retake is being recorded against an existing one. */
  mode?: 'full' | 'attempt';
}

/** "10 Dec 2026" — short and unambiguous, avoiding the day/month ordering question. */
const formatDate = (iso?: string): string => {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(iso || ''));
  if (!match) return '—';
  const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${Number(match[3])} ${MONTHS[Number(match[2]) - 1]} ${match[1]}`;
};

const CertificationForm: React.FC<Props> = ({
  value,
  onChange,
  ownerEmail,
  errors = [],
  disabled = false,
  mode = 'full',
}) => {
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');

  const failed = value.result === CertificationResult.FAILED;

  /*
   * The deadline is shown as soon as a failed result has a date, before anything is submitted.
   *
   * The requirement is that it be displayed clearly, and the moment it actually matters to the
   * employee is while they are filling the form — not after they have submitted and gone away.
   * Derived rather than stored in state so it cannot fall out of step with the date above it.
   */
  const retakeDeadline = useMemo(
    () => (failed && value.resultDate ? calculateRetakeDeadline(value.resultDate) : undefined),
    [failed, value.resultDate]
  );

  const handleFile = async (file?: File | null) => {
    if (!file) return;
    setUploadError('');
    const problem = validateCertificateFile(file);
    if (problem) {
      setUploadError(problem);
      return;
    }
    setUploading(true);
    try {
      const { key, fileName } = await uploadCertificate(file, ownerEmail);
      onChange({ ...value, documentKey: key, documentName: fileName });
    } catch (error: any) {
      /*
       * The key is NOT set when the upload fails.
       *
       * Setting it optimistically would let the record save with a reference to an object that
       * was never stored — which is precisely the defect pmsAttachments was written to fix, and
       * it surfaces as an approver clicking a certificate and getting nothing.
       */
      setUploadError(error?.message || 'The document could not be uploaded. Try again.');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="cert-form">
      {mode === 'full' && (
        <div className="cert-form__row">
          <div className="cert-form__field">
            <label htmlFor="cert-name">Certification name *</label>
            <input
              id="cert-name"
              type="text"
              value={value.name || ''}
              disabled={disabled}
              placeholder="AWS Certified Solutions Architect"
              onChange={(e) => onChange({ ...value, name: e.target.value })}
            />
          </div>
          <div className="cert-form__field">
            <label htmlFor="cert-provider">Provider *</label>
            <input
              id="cert-provider"
              type="text"
              value={value.provider || ''}
              disabled={disabled}
              placeholder="Amazon Web Services"
              onChange={(e) => onChange({ ...value, provider: e.target.value })}
            />
          </div>
        </div>
      )}

      <div className="cert-form__row">
        {/*
          * Result, as two radios rather than a select.
          *
          * Both options are visible without opening anything, which matters because what the
          * form asks for changes with the answer — a failed result reveals a deadline and keeps
          * the document mandatory, and a control that hides one of its two values makes that
          * consequence harder to anticipate.
          */}
        <div className="cert-form__field">
          <span className="cert-form__label">Certification result *</span>
          <div className="cert-form__radios">
            {[CertificationResult.PASSED, CertificationResult.FAILED].map((option) => (
              <label
                key={option}
                className={`cert-form__radio${value.result === option ? ' is-selected' : ''}`}
              >
                <input
                  type="radio"
                  name="cert-result"
                  value={option}
                  checked={value.result === option}
                  disabled={disabled}
                  onChange={() => onChange({ ...value, result: option })}
                />
                {option}
              </label>
            ))}
          </div>
          <small className="cert-form__hint">
            Whether you passed the exam. This is not the approval status of the reimbursement.
          </small>
        </div>
      </div>

      <div className="cert-form__row">
        <div className="cert-form__field">
          <label htmlFor="cert-exam-date">Exam / written date</label>
          <input
            id="cert-exam-date"
            type="date"
            value={value.examDate || ''}
            disabled={disabled}
            onChange={(e) => onChange({ ...value, examDate: e.target.value })}
          />
        </div>
        <div className="cert-form__field">
          <label htmlFor="cert-result-date">Result date *</label>
          <input
            id="cert-result-date"
            type="date"
            value={value.resultDate || ''}
            disabled={disabled}
            onChange={(e) => onChange({ ...value, resultDate: e.target.value })}
          />
          <small className="cert-form__hint">
            {failed
              ? 'The retake deadline is calculated from this date.'
              : 'The day the result was published.'}
          </small>
        </div>
      </div>

      {/*
        * Expiry, shown only for a PASS.
        *
        * A failed attempt has nothing to lapse, and offering the field would invite somebody to
        * fill in a date that the model then has to ignore. Optional even for a pass — plenty of
        * qualifications never expire, and a blank means exactly that rather than "unknown".
        */}
      {!failed && (
        <div className="cert-form__row">
          <div className="cert-form__field">
            <label htmlFor="cert-expiry">Expiry date</label>
            <input
              id="cert-expiry"
              type="date"
              value={value.expiryDate || ''}
              disabled={disabled}
              min={value.resultDate || undefined}
              onChange={(e) => onChange({ ...value, expiryDate: e.target.value })}
            />
            <small className="cert-form__hint">
              Leave blank if it does not expire. Most vendor certifications lapse after two or
              three years — the date is on the certificate.
            </small>
          </div>
        </div>
      )}

      {failed && (
        <div className={`cert-form__deadline${retakeDeadline ? '' : ' is-pending'}`}>
          {retakeDeadline ? (
            <>
              <strong>Retake deadline: {formatDate(retakeDeadline)}</strong>
              <span>
                Three calendar months from the result date. The exam must be retaken on or before
                this day.
              </span>
            </>
          ) : (
            <span>Enter the result date to see the retake deadline.</span>
          )}
        </div>
      )}

      <div className="cert-form__field">
        <label htmlFor="cert-doc">
          {failed ? 'Result document *' : 'Certificate *'}
        </label>
        {/*
          * Mandatory for a FAILED result too, and the label says so.
          *
          * This is the rule most likely to be read as a mistake — "I failed, there is no
          * certificate" — so the hint names what is wanted instead of only insisting.
          */}
        <input
          id="cert-doc"
          type="file"
          accept={ALLOWED_CERTIFICATE_TYPES.map((t) => `.${t}`).join(',')}
          disabled={disabled || uploading}
          onChange={(e) => handleFile(e.target.files?.[0])}
        />
        <small className="cert-form__hint">
          {failed
            ? 'Required. Upload the score report or result letter showing the attempt and its outcome.'
            : 'Required. Upload the pass certificate.'}
          {' '}
          {ALLOWED_CERTIFICATE_TYPES.join(', ').toUpperCase()}, up to {MAX_CERTIFICATE_SIZE_MB}MB.
        </small>
        {uploading && <p className="cert-form__uploading">Uploading…</p>}
        {value.documentName && !uploading && (
          <p className="cert-form__uploaded">Attached: {value.documentName}</p>
        )}
        {uploadError && <p className="cert-form__error">{uploadError}</p>}
      </div>

      {errors.length > 0 && (
        <ul className="cert-form__errors" role="alert">
          {errors.map((message) => (
            <li key={message}>{message}</li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default CertificationForm;
